import { useState, type ReactNode } from 'react';
import { sx } from './designStyles';

/**
 * The V9 "i" button (design v9iTog, change log "V9 started"): grey explainer paragraphs open
 * on click instead of always taking the room. Empty states and legal or honesty notices are
 * never put behind it; they stay visible.
 */
export const ABOUT_BTN =
  'display:inline-flex;align-items:center;justify-content:center;flex:0 0 auto;width:22px;height:22px;padding:0;border-radius:50%;border:1px solid rgba(34,39,46,.30);background:transparent;font-family:Georgia,serif;font-style:italic;font-size:14px;font-weight:700;line-height:1;color:#3C454F;text-decoration:none;vertical-align:middle;cursor:pointer;';

/** The button alone, for a heading that lays out its own text (pair it with `open`). */
export function AboutButton({ open, onToggle, label = 'About this', gap }: { open: boolean; onToggle: () => void; label?: string; gap?: 6 | 8 }) {
  // After a heading's text the design spaces it 6px, or 8px with its own letter spacing
  // (inside a spaced capital label).
  const after = gap === 6 ? 'margin-left:6px;' : gap === 8 ? 'margin-left:8px;letter-spacing:0;' : '';
  return (
    <button type="button" aria-label={label} title={label} aria-expanded={open} onClick={onToggle} style={sx(ABOUT_BTN + after)}>
      i
    </button>
  );
}

/**
 * A heading line with the "i" after it, and the explainer under it while open.
 * `textStyle` is the explainer's own style (the design keeps each one's size and color).
 */
export function AboutThis({ head, children, textStyle = 'font-size:14px;color:#54606C;line-height:1.45;margin-top:6px;', testId, inline = false }: { head?: ReactNode; children: ReactNode; textStyle?: string; testId?: string; inline?: boolean }) {
  const [open, setOpen] = useState(false);
  if (inline) {
    // The design's bare form: the "i", then the text beside it once opened.
    return (
      <div data-testid={testId} style={sx('display:flex;align-items:flex-start;gap:4px;min-width:0;')}>
        <AboutButton open={open} onToggle={() => setOpen((o) => !o)} />
        {open && <div style={sx('flex:1 1 auto;min-width:0;' + textStyle)}>{children}</div>}
      </div>
    );
  }
  return (
    <div data-testid={testId} style={sx('min-width:0;')}>
      <div style={sx('display:flex;align-items:center;gap:6px;flex-wrap:wrap;min-width:0;')}>
        {head}
        <AboutButton open={open} onToggle={() => setOpen((o) => !o)} />
      </div>
      {open && <div style={sx(textStyle)}>{children}</div>}
    </div>
  );
}
